import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import Login from '../../pages/login/Login';
import { useCallback, useContext, useEffect, useState } from 'react';
import UserContext from '../../context/user/UserContext';
import Home from '../../pages/home/Home';
import Wizard from '../../pages/wizard/wizard/Wizard';
import DocumentTypeList from '../../pages/document-type/document-type-list/DocumentTypeList';
import DocumentTypeForm from '../../pages/document-type/document-type-form/DocumentTypeForm';
import TenantForm from '../../pages/tenant/tenant-form/TenantForm';
import MessageTemplateList from '../../pages/message-template/MessageTemplateList';
import FollowupList from '../../pages/followup/FollowupList';
import EmployeeSearch from '../../pages/internal-medical-certificate/employee-search/EmployeeSearch';
import MedicalCertificateForm from '../../pages/internal-medical-certificate/medical-certificate-form/MedicalCertificateForm';
import MessageTemplateForm from '../../pages/message-template/MessageTemplateForm';
import { user } from '@seniorsistemas/senior-platform-data';
import IUser from '../../interfaces/IUser';
import { useSessionStorage } from '../../hooks/providers/useSessionStorage';
import Loading from '../../pages/loading/Loading';
import { useAuthorizationService } from '../../hooks/services/useAuthorizationService';
import AppError from '../../errors/AppError';
import { useToastrVanila } from '../../hooks/util/useToastrVanila';
import PointSituation from '../../pages/point-situation/PointSituation';
import PointSituationForm from '../../pages/point-situation/point-situation-form/PointSituationForm';
import CollaboratorList from '../../pages/collaborator/list-collaborators/CollaboratorList';
import CollaboratorForm from '../../pages/collaborator/collaborator-form/CollaboratorForm';
import FollowupPointControl from '../../pages/followup-point-control/followupPointControl/FollowupPointControl';
import PointControlList from '../../pages/followup-point-control/pointControlList/PointControlList';
import PointControlForm from '../../pages/followup-point-control/pointControlForm/PointControlForm';
import CompaniesList from '../../pages/campanies/companies-list/CompaniesList';
import CompanyForm from '../../pages/campanies/company-form/CompanyForm';
import InternalUserList from '../../pages/internal-user/InternalUserList';
import InternalUserForm from '../../pages/internal-user/InternalUserForm';
import InternalUserAccessScopeList from '../../pages/internal-user/InternalUserAccessScopeList';
import FollowupReportList from '../../pages/followupReport/FollowupReportList';
import DocumentStatus from '../../pages/documentStatus/ReadDocumentStats/DocumentStatus';
import ConsultProtocolDocument from '../../pages/documentStatus/ConsultDocumentStats/ConsultProtocolDocument';
import ViewMedicalCertificate from '../../pages/wizard/viewMedicalCertificates/ViewMedicalCertificate';
import Logs from '../../pages/logs/Logs';
import { hasAccessTo } from '../../util/hasAccessTo';

const AppRouter = () => {
  const [loading, setLoading] = useState(false);
  const location = useLocation();

  const toastr = useToastrVanila();

  const { state, setState: setUserState } = useContext(UserContext);
  const sessionStorage = useSessionStorage();
  const authorization = useAuthorizationService();

  const handleAuthenticatePlatform = useCallback(async () => {
    setLoading(true);
    if (location.pathname === '/' || location.pathname === '/login') {
      setLoading(false);
    } else if (state.logged) {
      setLoading(false);
    } else {
      await user
        .getToken()
        .then(async token => {
          const userState: IUser = {
            access_token: token.access_token,
            email: token.email,
            expires_in: token.expires_in,
            logged: true,
            name: token.fullName?.replaceAll('+', ' '),
            refresh_token: token.refresh_token,
            tenantDomain: token.tenantDomain,
            username: token.username,
            typeAuth: 'platform',
            resources: [''],
            resourcesWithActions: {},
            fullUser: false,
            rhUser: false,
            managerUser: false,
          };

          await authorization
            .getUserResources(`Bearer ${token.access_token}`)
            .then(response => {
              // Usar o JWT da aplicação retornado pelo backend
              if (response.access_token) {
                userState.access_token = response.access_token;
              }
              userState.resourcesWithActions = response.resources || response;
            })
            .catch(error => {
              throw new AppError(error.message);
            });

          sessionStorage.setItem('user', userState);
          setUserState({ ...userState });
          return userState;
        })
        .catch(error => {
          const isSeniorError =
            error.message ===
            'Não foi possível recuperar os dados da Senior Platform. Tente obter um token na inicialização.';

          if (!isSeniorError) {
            toastr.error('Acesso negado. Verificar com o administrador do sistema.');
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, []);

  useEffect(() => {
    handleAuthenticatePlatform();
  }, []);

  return (
    <Routes>
      {state.logged ? (
        <>
          <Route path="/home" element={<Home title="Atestado | Home" />} />

          {hasAccessTo(["cadastrar-documento-atestado-web"], state.resourcesWithActions) && (
            <>
              <Route path="/cadastrar-documento" element={<EmployeeSearch title="Atestado | Novo Documento" />} />
            </>
          )}

          {hasAccessTo(["cadastrar-documento-atestado-web"], state.resourcesWithActions) && (
            <>
              <Route
                path="/cadastrar-documento/:id/:employeeId/new"
                element={<MedicalCertificateForm title="Atestado | Novo Documento" />}
              />
              <Route
                path="/cadastrar-documento/:id/:employeeId/edit"
                element={<MedicalCertificateForm title="Atestado | Editar Documento" />}
              />
            </>
          )}

          {hasAccessTo(["followup-atestado-web"], state.resourcesWithActions) && (
            <>
              <Route path="/followup" element={<FollowupList title="Atestado | Followup" />} >
                <Route path="editar-atestado/:id" element={null} />
              </Route>

              <Route path="/followup-point-control" element={<FollowupPointControl title="Ponto | Followup" />} />
            </>
          )}

          {hasAccessTo(["relatório-atestado-web"], state.resourcesWithActions) && (
            <Route path="/followup-report" element={<FollowupReportList title="Atestado | Relatório" />} />
          )}

          {hasAccessTo(["atestado-digital-admin"], state.resourcesWithActions) && (
            <>

              {hasAccessTo(["manutencao-usuario-atestado-web"], state.resourcesWithActions) && (
                <>
                  <Route path="/user" element={<InternalUserList title="Atestado | Usuários" />} />
                </>
              )}

              {hasAccessTo(["manutencao-usuario-atestado-web"], state.resourcesWithActions) && (
                <>
                  <Route
                    path="/user-scope/:userId"
                    element={<InternalUserAccessScopeList title="Atestado | Usuários" />}
                  />
                  <Route path="/user/:id/new" element={<InternalUserForm title="Atestado | Novo Usuário" />} />
                  <Route path="/user/:id/edit" element={<InternalUserForm title="Atestado | Editar Usuário" />} />
                </>
              )}

              {hasAccessTo(["parametros-empresa-atestado-web"], state.resourcesWithActions) && (
                <>
                  <Route path="/tenant" element={<TenantForm title="Atestado | Empresa" />} />
                  <Route path="/company" element={<CompaniesList title="Atestado | Empresa" />} />
                </>
              )}

              {hasAccessTo(["parametros-empresa-atestado-web"], state.resourcesWithActions) && (
                <>
                  <Route path="/company/:id/new" element={<CompanyForm title="Atestado | Empresa" />} />
                  <Route path="/company/:id/edit" element={<CompanyForm title="Atestado | Empresa" />} />
                </>
              )}

              {hasAccessTo(["logs-atestado-web"], state.resourcesWithActions) && (
                <>
                  <Route path="/logs" element={<Logs title="Email | Whatsapp" />} />
                </>
              )}

              <Route
                path="/point-control-list/:date/:id"
                element={<PointControlList title="Ponto | Listar Pontos" />}
              />
              <Route
                path="/point-control-form/:date/:id"
                element={<PointControlForm title="Ponto | Editar Ponto" />}
              />

              <Route path="/spoint-situation" element={<PointSituation title="Atestado | Situações do Ponto" />} />
              <Route
                path="/point-situation/:id/edit"
                element={<PointSituationForm title="Atestado | Situações do Ponto" />}
              />
            </>
          )}

          {hasAccessTo(["atestado-digital-gestor", "atestado-digital-rh", "atestado-digital-admin"], state.resourcesWithActions) && (
            <>
              {hasAccessTo(["tipo-documento-atestado-web"], state.resourcesWithActions) && (
                <>
                  <Route path="/tipo-documento" element={<DocumentTypeList title="Atestado | Documento" />} />
                </>
              )}

              {hasAccessTo(["tipo-documento-atestado-web"], state.resourcesWithActions) && (
                <>
                  <Route
                    path="/tipo-documento/:id/edit"
                    element={<DocumentTypeForm title="Atestado | Editar Documento" />}
                  />
                  <Route
                    path="/tipo-documento/:id/new"
                    element={<DocumentTypeForm title="Atestado | Incluir Documento" />}
                  />
                </>
              )}

              {hasAccessTo(["modelos-mensagem-atestado-web"], state.resourcesWithActions) && (
                <>
                  <Route path="/modelo-mensagem" element={<MessageTemplateList title="Atestado | Modelo Mensagem" />} />
                </>
              )}

              {hasAccessTo(["modelos-mensagem-atestado-web"], state.resourcesWithActions) && (
                <>
                  <Route
                    path="/modelo-mensagem/:id/new"
                    element={<MessageTemplateForm title="Atestado | Modelo Mensagem" />}
                  />
                  <Route
                    path="/modelo-mensagem/:id/edit"
                    element={<MessageTemplateForm title="Atestado | Modelo Mensagem" />}
                  />
                </>
              )}

              {hasAccessTo(["colaboradores-atestado-web"], state.resourcesWithActions) && (
                <>
                  <Route path="/collaborator" element={<CollaboratorList title="Atestado | Colaborador" />} />
                </>
              )}

              {hasAccessTo(["colaboradores-atestado-web"], state.resourcesWithActions) && (
                <>
                  <Route
                    path="/collaborator/:id/edit"
                    element={<CollaboratorForm title="Atestado | Editar Colaborador" />}
                  />
                  <Route
                    path="/collaborator/:id/new"
                    element={<CollaboratorForm title="Atestado | Incluir Colaborador" />}
                  />
                </>
              )}
            </>
          )}
          <Route path="*" element={<Navigate to="/home" />} />
        </>
      ) : (
        <>
          {loading ? (
            <Route path="*" element={<Loading title="Atestado WEB" />} />
          ) : (
            <>
              <Route path="/" element={<Wizard title="Atestado WEB" />} />
              <Route path="/login" element={<Login title="Atestado | Login" />} />
              <Route path="*" element={<Navigate to="/" />} />
              <Route
                path="/consulta/:numberProtocol"
                element={<DocumentStatus title="Atestado | Status do Documento" />}
              />
              <Route
                path="/atestados-status"
                element={<ViewMedicalCertificate title="Atestado | Status do Documento" />}
              />
              <Route path="/consulta" element={<ConsultProtocolDocument title="Atestado | Consulta por Protocolo" />} />
            </>
          )}
        </>
      )}
    </Routes>
  );
};

export default AppRouter;
